import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicPageShell, { BulletList, SectionHeading } from '@/components/PublicPageShell';
import PopiaBadge from '@/components/PopiaBadge';
import { GraduationCap, Users, Shield, ArrowRight } from 'lucide-react';

const SCHOOL_FEATURES = [
  "Full squad management — track every player's development, attendance, and fitness.",
  "Digital match sheets — no more paper; stats captured in real time.",
  "Individual Development Plans (IDPs) per player with parent visibility.",
  "Injury management and return-to-play protocols built in.",
  "Put your school's talent on the national map — scouts can find your players.",
  "Coach-to-parent messaging and parent portal included.",
  "Community voucher programme available for EMIS-verified public schools.",
];

const CLUB_FEATURES = [
  "Complete club operating infrastructure: squads, fixtures, seasons, stats.",
  "Video analysis and telestration tools for coaches.",
  "Load management, wellness monitoring, and injury tracking.",
  "Digital playbook and tactical board for match preparation.",
  "Community fundraising module for equipment, facilities, and events.",
  "Squad and opposition analytics dashboards.",
  "Player pipeline tracking from junior to senior across all age groups.",
];

const UNION_FEATURES = [
  "Province-wide player visibility — every verified player in your province, searchable.",
  "Representative pathway management from age-group to senior.",
  "Centralised performance benchmarking across all affiliated clubs and schools.",
  "Agency/union scout tier for multi-user recruitment and talent tracking.",
  "Analytics and reporting tools for union administrators and selectors.",
  "Community fundraising for provincial development programmes and bursaries.",
];

function Tier({ icon: Icon, title, price, features, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-7 hover:border-[#A3FF00]/30 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon className="w-7 h-7 text-[#A3FF00]" strokeWidth={1.5} />
          <h3 className="text-2xl font-heading font-bold">{title}</h3>
        </div>
      </div>
      <p className="text-3xl font-heading font-bold text-white mb-1">{price}</p>
      <p className="text-zinc-500 text-sm mb-6">per player / per month</p>
      <BulletList items={features} />
    </div>
  );
}

export default function ForSchools() {
  const navigate = useNavigate();
  return (
    <PublicPageShell
      eyebrow="FOR SCHOOLS, CLUBS & UNIONS"
      title="The full operational stack for institutional rugby"
      intro="Manage squads, develop talent, fundraise, and put your players on the national talent map — all from one platform at accessible per-player pricing."
      dataTestId="for-schools-page"
    >
      <PopiaBadge className="mb-8" dataTestId="schools-popia-badge" />
      <div className="grid lg:grid-cols-3 gap-5">
        <Tier icon={GraduationCap} title="Schools" price="R19" features={SCHOOL_FEATURES} testid="tier-schools" />
        <Tier icon={Users} title="Clubs" price="R39" features={CLUB_FEATURES} testid="tier-clubs" />
        <Tier icon={Shield} title="Provincial Unions" price="R70" features={UNION_FEATURES} testid="tier-unions" />
      </div>

      <div className="mt-10 bg-gradient-to-br from-[#0a0a0a] to-[#0a1f15] border border-[#A3FF00]/30 rounded-2xl p-8" data-testid="voucher-banner">
        <p className="text-[#A3FF00] text-xs font-bold tracking-[0.25em] mb-2">COMMUNITY ACCESS VOUCHER PROGRAMME</p>
        <h3 className="text-2xl font-heading font-bold mb-2">Free access for qualifying EMIS-verified public schools</h3>
        <p className="text-zinc-300 mb-4 leading-relaxed">
          Qualifying public schools receive full platform access at no cost. Email <a href="mailto:info@futurexv.co.za" className="text-[#A3FF00]">info@futurexv.co.za</a> with
          your EMIS number to request a voucher.
        </p>
      </div>

      <SectionHeading>Why institutions choose FutureXV</SectionHeading>
      <div className="space-y-4 text-zinc-300 leading-relaxed">
        <p>
          Most schools and clubs have no digital tools for player tracking, performance benchmarking, injury
          management, or development planning. Coaches cannot measure progress. Administrators cannot report
          meaningfully. Talent slips through unnoticed.
        </p>
        <p>
          FutureXV closes that gap with one accessible per-player price — giving every institution professional-grade
          development tools, talent discovery infrastructure, and fundraising support.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        <Button onClick={() => navigate('/auth?role=school_club')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-8 py-6" data-testid="schools-register-cta">
          Register Your School or Club <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <Button onClick={() => navigate('/pricing')} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 px-8 py-6">
          View full pricing
        </Button>
      </div>
    </PublicPageShell>
  );
}
